import { AfterViewChecked, Component, ElementRef, HostListener, NgZone, OnDestroy, OnInit, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UserService } from '../../../Dashboard/_services/users.service';
import { environment } from '../../../../environments/environment';
import { pageByKey, pageKeyForPath } from '../../_constants/page-access';
import { renderNuaiMarkdown } from './nuai-markdown';
import { NustoneMarkComponent } from './nustone-mark.component';

interface ChatMessage {
  from: 'me' | 'nuai';
  text: string;
  html?: string;
  toolsUsed?: string[];
  error?: boolean;
  copied?: boolean;
  streaming?: boolean;
  stopped?: boolean;
}

interface PageContext {
  label: string; // shown in the panel, e.g. "Order #99219290"
  detail: string; // sent to NuSI
  suggestions: string[];
}

const STORE_KEY = 'nuai.chat';

// Plain-English names for what NuSI looked up, shown under its answers.
const TOOL_LABELS: Record<string, string> = {
  search_products: 'products',
  get_product: 'product details',
  find_customers: 'customers',
  get_customer: 'customer history',
  find_orders: 'orders',
  get_order: 'order details',
  get_leads: 'lead lists',
  get_sales_ideas: 'sales ideas',
  recommend_products: 'recommendations',
  low_stock: 'stock levels',
  search_website: 'website content',
  sales_report: 'sales figures',
  product_views_report: 'product views',
};

const GENERAL_SUGGESTIONS = [
  'Who should I call next, and what should I say?',
  'Price, stock and coverage of Kandla Grey porcelain 600x900',
  'Which products are running low on stock?',
  'Write a friendly WhatsApp to a customer whose samples have arrived',
  'What does the website say about returns?',
];

// NuSI: the back-office chat assistant. Lives at the app root so it's there
// on every page (New sale included) and keeps the chat while staff move
// around; the chat is also kept for the browser session. Answers stream in
// as they're written, and NuSI is told which page the agent is on (order,
// product, New sale basket) so "this order" just works. Read-only - it looks
// things up and drafts text, it never changes anything.
@Component({
    selector: 'app-nuai-chat',
    imports: [FormsModule, MatIconModule, MatTooltipModule, NustoneMarkComponent],
    templateUrl: './nuai-chat.component.html',
    styleUrl: './nuai-chat.component.css'
})
export class NuaiChatComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('scroller') scroller?: ElementRef<HTMLDivElement>;
  @ViewChild('box') box?: ElementRef<HTMLTextAreaElement>;

  private platformId = inject(PLATFORM_ID);
  visible = false; // signed in, and not on a sign-in page
  open = false;
  wide = false;
  sending = false;
  status = ''; // what NuSI is looking up right now
  draft = '';
  messages: ChatMessage[] = [];
  page: PageContext = { label: '', detail: '', suggestions: GENERAL_SUGGESTIONS };
  firstName = '';
  peek = false; // the "Hi! I'm NuSI" bubble, once per browser session
  private conversationId: string | null = null;
  private scrollPending = false;
  private owner = ''; // who the saved chat belongs to
  private abort: AbortController | null = null;

  // Keeping the launcher clear of the page's own bottom-corner controls.
  lift = 0; // px moved up, to sit above a save bar / action bar
  coveredByPanel = false; // a pop-up or slide-over has the corner: step aside
  private avoidTimer: any = null;
  private avoidCleanup: (() => void)[] = [];

  constructor(
    private router: Router,
    private userService: UserService,
    private zone: NgZone,
    private host: ElementRef<HTMLElement>,
  ) {}

  ngOnDestroy(): void {
    this.avoidCleanup.forEach((f) => f());
    clearTimeout(this.avoidTimer);
  }

  // ---- staying out of the way --------------------------------------------
  // The launcher sits in the bottom-right corner, where many pages put their
  // own Save / action bars and where slide-over panels and pop-ups open.
  // Rather than nudging page by page, look at what is actually in that
  // corner: a bar there (fixed or sticky, not tall) -> sit just above it; a
  // panel or pop-up (tall, or a full-screen backdrop) -> fade out until it
  // closes. Re-checked on navigation, scroll, resize and DOM changes,
  // throttled and outside Angular so it never slows the page.
  private watchCorner(): void {
    this.zone.runOutsideAngular(() => {
      const schedule = () => {
        if (this.avoidTimer) return;
        this.avoidTimer = setTimeout(() => {
          this.avoidTimer = null;
          this.measureCorner();
        }, 150);
      };
      const observer = new MutationObserver(schedule);
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] });
      window.addEventListener('resize', schedule);
      window.addEventListener('scroll', schedule, true);
      // Catches CSS transitions that end without a DOM change.
      const interval = setInterval(schedule, 2000);
      this.avoidCleanup.push(
        () => observer.disconnect(),
        () => window.removeEventListener('resize', schedule),
        () => window.removeEventListener('scroll', schedule, true),
        () => clearInterval(interval),
      );
      schedule();
    });
  }

  private measureCorner(): void {
    if (!this.visible || this.open) return;
    const w = window.innerWidth;
    const h = window.innerHeight;
    const own = this.host.nativeElement;
    let lift = 0;
    let covered = false;
    const checked = new Set<Element>();
    // Points across where the launcher sits when not lifted (20px from the
    // corner, about 80x44) plus a little margin for its glow.
    for (const x of [w - 22, w - 60, w - 104]) {
      for (const y of [h - 22, h - 44, h - 68]) {
        for (const el of document.elementsFromPoint(x, y)) {
          if (own.contains(el) || el === document.body || el === document.documentElement) continue;
          const found = this.floatingBox(el);
          if (!found || checked.has(found.box)) continue;
          checked.add(found.box);
          const r = found.box.getBoundingClientRect();
          if (r.width === 0 || r.height === 0) continue;
          if (r.height > h * 0.45) {
            // Tall and fixed: a slide-over, pop-up or backdrop. Tall and
            // sticky is a page column (e.g. New sale's basket) - leave it be.
            if (found.fixed) covered = true;
          } else if (r.bottom > h - 90) {
            // A bar at the bottom: clear its top edge by 12px.
            lift = Math.max(lift, Math.ceil(h - r.top + 12 - 20));
          }
        }
      }
    }
    lift = Math.min(lift, Math.round(h * 0.4));
    if (lift !== this.lift || covered !== this.coveredByPanel) {
      this.zone.run(() => {
        this.lift = lift;
        this.coveredByPanel = covered;
      });
    }
  }

  // The nearest fixed or sticky element containing el (a bar, panel, pop-up).
  private floatingBox(el: Element): { box: Element; fixed: boolean } | null {
    for (let n: Element | null = el; n && n !== document.body; n = n.parentElement) {
      const pos = getComputedStyle(n).position;
      if (pos === 'fixed' || pos === 'sticky') return { box: n, fixed: pos === 'fixed' };
    }
    return null;
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    this.restore();
    this.onRoute();
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => this.onRoute());
    this.watchCorner();
    // Say hello once per session, a few seconds in, then get out of the way.
    if (!this.peekSeen()) {
      setTimeout(() => {
        if (this.visible && !this.open && !this.peekSeen()) {
          this.peek = true;
          setTimeout(() => this.dismissPeek(), 9000);
        }
      }, 3500);
    }
  }

  dismissPeek(): void {
    this.peek = false;
    try {
      sessionStorage.setItem('nuai.peekSeen', '1');
    } catch {
      // per-session nicety only
    }
  }

  private peekSeen(): boolean {
    try {
      return sessionStorage.getItem('nuai.peekSeen') === '1';
    } catch {
      return false;
    }
  }

  ngAfterViewChecked(): void {
    if (this.scrollPending && this.scroller) {
      this.scroller.nativeElement.scrollTop = this.scroller.nativeElement.scrollHeight;
      this.scrollPending = false;
    }
  }

  private onRoute(): void {
    const path = this.router.url.split('?')[0];
    const signInPage = ['/login', '/forgot-password', '/klarna-confirmation'].some((p) => path.startsWith(p));
    const me = this.userService.isLoggedIn();
    this.visible = !signInPage && !!me?.isLoggedIn;
    if (!this.visible) this.open = false;
    // Someone else signed in on this tab: their chat, not the last person's.
    const who = me?.isLoggedIn ? String(me.userName ?? '') : '';
    if (who && who !== this.owner) {
      if (this.owner) this.newChat();
      this.owner = who;
      this.save();
    }
    this.firstName = who.split(/[\s@._]/)[0] ?? '';
    if (this.firstName) this.firstName = this.firstName.charAt(0).toUpperCase() + this.firstName.slice(1);
    this.page = this.pageContext(path);
  }

  // Where the agent is, for NuSI and for the suggested questions.
  private pageContext(path: string): PageContext {
    const order = path.match(/^\/sales\/(\d{5,})$/);
    if (order) {
      return {
        label: `Order #${order[1]}`,
        detail: `the order page for order #${order[1]}`,
        suggestions: ['Summarise this order', 'Has this customer ordered before? What do they usually buy?', 'Write a friendly update to the customer about this order', 'What could I add to this order for them?'],
      };
    }
    if (path.startsWith('/sales/create')) {
      const basket = this.basket();
      return {
        label: basket.length ? `New sale · ${basket.length} item${basket.length === 1 ? '' : 's'}` : 'New sale',
        detail: basket.length ? `the New sale page; basket: ${basket.join('; ')}` : 'the New sale page (basket empty)',
        suggestions: basket.length
          ? ['What goes well with this basket?', 'Is everything in this basket in stock?', 'Write a quote summary of this basket for the customer', 'Any cheaper alternatives for this basket?']
          : ['What should I suggest to a customer doing a new patio?', 'Which products are on offer right now?', 'How much porcelain do I need for 25 m²?', ...GENERAL_SUGGESTIONS.slice(0, 1)],
      };
    }
    const product = path.match(/^\/products\/edit\/([^/]+)$/);
    if (product) {
      return {
        label: `Product: ${product[1]}`,
        detail: `editing the product with slug "${product[1]}"`,
        suggestions: ['How is this product selling, and how much stock is left?', 'What is usually bought with this product?', 'Write a short, punchy description of this product'],
      };
    }
    const key = pageKeyForPath(path);
    const label = (key && pageByKey(key)?.label) || '';
    if (path === '/sales') {
      return { label: 'Sales orders', detail: 'the Sales orders page', suggestions: ['Who should I call next, and what should I say?', 'Any unpaid orders from the last few days?', 'Which abandoned carts are worth chasing today?', ...GENERAL_SUGGESTIONS.slice(1, 3)] };
    }
    return { label, detail: label ? `the ${label} page` : '', suggestions: GENERAL_SUGGESTIONS };
  }

  // What's in the New sale basket (kept by the sale page in localStorage).
  private basket(): string[] {
    try {
      const cart = JSON.parse(localStorage.getItem('cartB') ?? 'null');
      return (cart?.items ?? []).slice(0, 15).map((i: any) => `${i.quantity} x ${i.name}${i.sku ? ` (SKU ${i.sku})` : ''}`);
    } catch {
      return [];
    }
  }

  get greeting(): string {
    const h = new Date().getHours();
    const part = h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
    return this.firstName ? `${part}, ${this.firstName}` : part;
  }

  toggle(): void {
    if (this.peek) this.dismissPeek();
    this.open = !this.open;
    if (!this.open) setTimeout(() => this.zone.runOutsideAngular(() => this.measureCorner()), 0);
    if (this.open) {
      this.page = this.pageContext(this.router.url.split('?')[0]); // basket may have changed
      this.scrollPending = true;
      setTimeout(() => this.box?.nativeElement.focus(), 50);
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.open && !this.sending) this.open = false;
  }

  newChat(): void {
    this.stop();
    this.messages = [];
    this.conversationId = null;
    this.save();
    setTimeout(() => this.box?.nativeElement.focus(), 50);
  }

  onKeydown(e: KeyboardEvent): void {
    // Enter sends; Shift+Enter is a new line.
    if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
      e.preventDefault();
      this.send();
    }
  }

  ask(text: string): void {
    this.draft = text;
    this.send();
  }

  stop(): void {
    this.abort?.abort();
  }

  async send(): Promise<void> {
    const text = this.draft.trim();
    if (!text || this.sending) return;
    this.page = this.pageContext(this.router.url.split('?')[0]);
    this.messages.push({ from: 'me', text });
    const reply: ChatMessage = { from: 'nuai', text: '', html: '', streaming: true };
    this.messages.push(reply);
    this.draft = '';
    this.sending = true;
    this.status = 'Thinking';
    this.scrollPending = true;
    this.save();

    this.abort = new AbortController();
    try {
      const res = await fetch(`${environment.apiUrl}/nuai/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(this.token() ? { Authorization: `Bearer ${this.token()}` } : {}) },
        body: JSON.stringify({ conversationId: this.conversationId, message: text, context: this.page.detail }),
        signal: this.abort.signal,
      });
      if (!res.ok || !res.body) {
        const body = await res.json().catch(() => null);
        throw new Error(res.status === 401 ? 'Your session has expired - please sign in again.' : body?.message || 'NuSI could not answer just now. Please try again.');
      }
      await this.readEvents(res.body, reply);
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        reply.stopped = true;
        if (!reply.text) this.setText(reply, '_Stopped._');
      } else {
        reply.error = true;
        this.setText(reply, e?.message || 'NuSI could not answer just now. Please try again.');
      }
    } finally {
      reply.streaming = false;
      this.sending = false;
      this.status = '';
      this.abort = null;
      this.scrollPending = true;
      this.save();
      setTimeout(() => this.box?.nativeElement.focus(), 50);
    }
  }

  // Server-Sent Events from /nuai/chat/stream: status, text, done, error.
  private async readEvents(body: ReadableStream<Uint8Array>, reply: ChatMessage): Promise<void> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let cut: number;
      while ((cut = buffer.indexOf('\n\n')) >= 0) {
        const chunk = buffer.slice(0, cut);
        buffer = buffer.slice(cut + 2);
        const event = chunk.match(/^event: (.+)$/m)?.[1];
        const data = chunk.match(/^data: (.+)$/m)?.[1];
        if (!event || !data) continue; // keep-alive comments
        const payload = JSON.parse(data);
        this.zone.run(() => {
          if (event === 'status') {
            this.status = payload.text;
          } else if (event === 'text') {
            this.status = '';
            this.setText(reply, reply.text + payload.t);
            this.scrollPending = true;
          } else if (event === 'done') {
            this.conversationId = payload.ended === 'conversation_full' ? null : payload.conversationId;
            this.setText(reply, payload.reply || reply.text);
            reply.toolsUsed = (payload.toolsUsed ?? []).map((t: string) => TOOL_LABELS[t] ?? t);
          } else if (event === 'error') {
            reply.error = true;
            this.setText(reply, payload.message);
          }
        });
      }
    }
  }

  private setText(m: ChatMessage, text: string): void {
    m.text = text;
    m.html = renderNuaiMarkdown(text);
  }

  private token(): string | null {
    try {
      const raw = localStorage.getItem('authTokenB') ?? sessionStorage.getItem('authTokenB');
      return raw ? JSON.parse(raw).access_token : null;
    } catch {
      return null;
    }
  }

  async copy(m: ChatMessage): Promise<void> {
    try {
      await navigator.clipboard.writeText(m.text);
      m.copied = true;
      setTimeout(() => (m.copied = false), 1500);
    } catch {
      // clipboard blocked - nothing to do
    }
  }

  // ---- browser-session memory (survives a page refresh) ----
  private save(): void {
    try {
      sessionStorage.setItem(
        STORE_KEY,
        JSON.stringify({
          owner: this.owner,
          conversationId: this.conversationId,
          messages: this.messages
            .filter((m) => !m.streaming)
            .slice(-60)
            .map(({ from, text, toolsUsed, error, stopped }) => ({ from, text, toolsUsed, error, stopped })),
        }),
      );
    } catch {
      // storage unavailable: the chat just won't survive a refresh
    }
  }

  private restore(): void {
    try {
      const saved = JSON.parse(sessionStorage.getItem(STORE_KEY) ?? 'null');
      if (!saved) return;
      this.owner = saved.owner ?? '';
      this.conversationId = saved.conversationId ?? null;
      this.messages = (saved.messages ?? []).map((m: ChatMessage) => ({ ...m, html: m.from === 'nuai' ? renderNuaiMarkdown(m.text) : undefined }));
    } catch {
      this.messages = [];
    }
  }
}
